import type { ColumnDef } from '@tanstack/react-table';
import { Building2, Calendar, DollarSign, Eye, FileText } from 'lucide-react';
import { useMemo } from 'react';

import type { IStoreIncomingMasterList } from '../../../api/IncomingInvoice.interface';

function useInvoicesListColumns() {
  return useMemo<ColumnDef<IStoreIncomingMasterList>[]>(
    () => [
      {
        accessorKey: 'ID',
        header: 'رقم الفاتورة',
        enableEditing: false,
        enableGrouping: false,
        size: 120,
        cell: ({ row }) => {
          return (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex items-center gap-3 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 px-4 py-3 shadow-sm dark:from-indigo-950/30 dark:to-purple-950/30">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md">
                  <FileText className="h-4 w-4 text-white" />
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    #{row?.original?.ID}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    فاتورة وارد
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'SupplierName',
        header: 'معلومات المورد',
        enableEditing: false,
        enableGrouping: false,
        size: 300,
        cell: ({ row }) => {
          return (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex w-full items-center gap-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-3 shadow-sm dark:from-emerald-950/30 dark:to-teal-950/30">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {row?.original?.SupplierName}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    {row?.original?.EmployeeName}
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'TrDate',
        header: 'تاريخ الفاتورة',
        enableEditing: false,
        enableGrouping: false,
        size: 150,
        cell: ({ row }) => {
          const date = new Date(row?.original?.TrDate);
          const formattedDate = date.toLocaleDateString('ar-SA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });

          return (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex items-center gap-3 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3 shadow-sm dark:from-amber-950/30 dark:to-orange-950/30">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 shadow-md">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {formattedDate}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    تاريخ الشراء
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'TotalInvoice',
        header: 'إجمالي الفاتورة',
        enableEditing: false,
        enableGrouping: false,
        size: 180,
        cell: ({ row }) => {
          const total = row?.original?.TotalInvoice || 0;
          const formattedTotal = new Intl.NumberFormat('ar-SA', {
            style: 'currency',
            currency: 'SAR',
            minimumFractionDigits: 2,
          }).format(total);

          return (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex items-center gap-3 rounded-xl bg-gradient-to-br from-fuchsia-50 to-pink-50 px-4 py-3 shadow-sm dark:from-fuchsia-950/30 dark:to-pink-950/30">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-600 shadow-md">
                  <DollarSign className="h-4 w-4 text-white" />
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {formattedTotal}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    إجمالي المبلغ
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'الإجراءات',
        enableEditing: false,
        enableGrouping: false,
        size: 120,
        cell: () => {
          return (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex items-center gap-2 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 px-4 py-3 shadow-sm dark:from-blue-950/30 dark:to-indigo-950/30">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                  <Eye className="h-4 w-4 text-white" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    عرض
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    التفاصيل
                  </div>
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );
}

export default useInvoicesListColumns;
